/**
 * Assign to Floor Rack Screen (2.11)
 * Inventory Module - Requirements
 * Connected to API with Add/Edit/Delete functionality
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Search, X, Save, RefreshCw, AlertCircle } from 'lucide-react';

// Mock data for development
const MOCK_DATA = [
  { id: 1, code: 'CODE001', name: 'Sample Item 1', status: 'Active' },
  { id: 2, code: 'CODE002', name: 'Sample Item 2', status: 'Active' },
  { id: 3, code: 'CODE003', name: 'Sample Item 3', status: 'Inactive' },
];

const AssignToFloorRackScreen = ({ theme = 'dark' }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    status: 'Active'
  });

  const isDark = theme === 'dark';
  const bgColor = isDark ? 'bg-slate-900' : 'bg-gray-50';
  const cardBg = isDark ? 'bg-slate-800' : 'bg-white';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const borderColor = isDark ? 'border-slate-700' : 'border-gray-200';
  const inputBg = isDark ? 'bg-slate-700' : 'bg-gray-50';

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(MOCK_DATA);
    } catch (err) {
      setError('Failed to load data');
      setData(MOCK_DATA);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAdd = () => {
    setEditItem(null);
    setFormData({ code: '', name: '', description: '', status: 'Active' });
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      code: item.code || '',
      name: item.name || '',
      description: item.description || '',
      status: item.status || 'Active'
    });
    setShowForm(true);
  };

  const handleDelete = (item) => {
    if (window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      setData(data.filter(d => d.id !== item.id));
    }
  };

  const handleSave = () => {
    if (editItem) {
      setData(data.map(d => d.id === editItem.id ? { ...d, ...formData } : d));
    } else {
      const newItem = { id: Date.now(), ...formData };
      setData([...data, newItem]);
    }
    setShowForm(false);
  };

  const filteredData = data.filter(item =>
    Object.values(item).some(val =>
      String(val).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <div className={`min-h-screen ${bgColor} ${textColor} p-6`} data-testid="assign-to-floor-rack-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Assign to Floor Rack</h1>
          <p className="text-sm text-gray-400">Screen ID: 2.11 | Inventory Module</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={loadData} className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white" data-testid="refresh-btn">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={handleAdd} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-btn">
            <Plus size={18} />
            Add New
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-3">
          <AlertCircle className="text-red-400" size={20} />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      <div className={`${cardBg} rounded-lg p-4 mb-6 border ${borderColor}`}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 rounded-lg ${inputBg} border ${borderColor} ${textColor}`}
            data-testid="search-input"
          />
        </div>
      </div>

      <div className={`${cardBg} rounded-lg border ${borderColor} overflow-hidden`}>
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
            <p>Loading...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="data-table">
              <thead className={isDark ? 'bg-slate-700' : 'bg-gray-100'}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">SL</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, index) => (
                  <tr key={item.id} className={`border-t ${borderColor} hover:${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                    <td className="px-4 py-3">{index + 1}</td>
                    <td className="px-4 py-3 font-mono text-sm">{item.code}</td>
                    <td className="px-4 py-3 font-medium">{item.name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        item.status === 'Active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleEdit(item)} className="p-1.5 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" data-testid={`edit-${item.id}`}>
                          <Edit2 size={14} />
                        </button>
                        <button onClick={() => handleDelete(item)} className="p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30" data-testid={`delete-${item.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredData.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-400">No data found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="form-modal">
          <div className={`${cardBg} rounded-xl p-6 w-full max-w-md border ${borderColor}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">{editItem ? 'Edit' : 'Add New'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${borderColor} ${textColor}`}
                  data-testid="form-code"
                  placeholder="Enter code"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${borderColor} ${textColor}`}
                  data-testid="form-name"
                  placeholder="Enter name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${borderColor} ${textColor}`}
                  rows={3}
                  data-testid="form-description"
                  placeholder="Enter description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg ${inputBg} border ${borderColor} ${textColor}`}
                  data-testid="form-status"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white" data-testid="form-cancel">
                Cancel
              </button>
              <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="form-save">
                <Save size={16} />
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignToFloorRackScreen;
